import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useReactiveVar } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { IconButton, Typography, TucarTheme, Button, Chip } from '@tucar/pit-stop';
import ChargingStationIcon from '@mui/icons-material/ChargingStation';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import getUniqueKey from '../../../../helpers/customKey';
import START_CHARGING_SESSION from '../../../../graphql/mutations/map/startChargingSession';
import { getDriverStore } from '../../../../graphql/store';
import {
  translateCurrentTypeToVelocity,
  getPlugIcon,
  plugIcons,
  getChipColorAndIconOcppStatus,
  translateOneStatus,
} from '../auxFunctions';
import AlertBanner from './alertBanner';

const fullScreenWrapper = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100dvh',
  zIndex: 10,
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  borderSizing: 'border-box',
  backgroundColor: '#F8F8F8',
};

const StationView = ({ pin, data, onBack }) => {
  // eslint-disable-next-line no-underscore-dangle
  const [connectorSelected, setConnectorSelected] = useState(pin._id);
  const { rent } = useReactiveVar(getDriverStore);
  const navigate = useNavigate();
  const { provider, addressAlias, address, connectors } = data || {};
  const allTypes = connectors.map((connector) => connector.currentType);
  const [errorMessage, setErrorMessage] = useState('');
  const uniqueTypes = allTypes.reduce((accumulator, currentType) => {
    if (!accumulator.includes(currentType)) {
      accumulator.push(currentType);
    }
    return accumulator;
  }, []);
  const [
    startCharging,
    { loading: startChargingLoading, data: startChargingData, error: startChargingError },
  ] = useMutation(START_CHARGING_SESSION.mutation, START_CHARGING_SESSION.policies);
  useEffect(() => {
    if (startChargingLoading) return;

    if (startChargingError) {
      setErrorMessage(`No se pudo iniciar la carga: ${startChargingError.message}`);
      return;
    }

    if (startChargingData) {
      navigate('./charging-view');
    }
  }, [startChargingLoading, startChargingData, startChargingError]);

  const handleStartCharge = () => {
    const carId = rent?.carId;
    if (!carId) {
      setErrorMessage('No tienes un vehículo asignado. No se puede iniciar la carga.');
      return;
    }
    startCharging({
      variables: {
        connectorId: connectorSelected,
        vehiclePlate: carId,
      },
    });
  };
  return (
    <Box sx={fullScreenWrapper}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          padding: '24px',
          backgroundColor: TucarTheme.colors.blue800,
          color: '#fff',
          borderRadius: '0px 0px 20px 20px',
          gap: '24px',
        }}
      >
        <Box sx={{ width: '100%' }}>
          <IconButton onClick={onBack} iconProps={{ name: 'leftArrowSpecial' }} />
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: '16px',
            alignItems: 'center',
          }}
        >
          <ChargingStationIcon
            sx={{
              fontSize: '72px',
              gridRow: '1 / 3',
              alignSelf: 'start',
            }}
          />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Typography variant="p" weight="semi-bold" fontSize={16}>
              {provider?.name || 'Proveedor Desconocido'}
            </Typography>
            <Typography variant="p" fontSize={12}>
              Estación de carga
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <LocationOnIcon sx={{ fontSize: '17px', color: TucarTheme.colors.blue400 }} />
              <Typography variant="p" fontSize={13}>
                {addressAlias || address || 'Dirección Desconocida'}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {uniqueTypes.map((type) => (
              <Box
                key={getUniqueKey()}
                sx={{
                  padding: '8px',
                  borderRadius: '6px',
                  border: '1px solid #fff',
                  width: 'fit-content',
                }}
              >
                <Typography variant="p" fontSize={12}>
                  {translateCurrentTypeToVelocity(type)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      {/* Info conectores */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          padding: '53px 27px',
          height: '100%',
          overflowY: 'auto',
        }}
      >
        {connectors?.map(({ _id, plugType, maxCapacity, currentType, ocppStatus }) => {
          const ocppStatusColorAndIIcon = getChipColorAndIconOcppStatus(ocppStatus);
          return (
            <Box
              key={_id}
              onClick={() => {
                if (ocppStatus !== 'Available' && ocppStatus !== 'Preparing') {
                  setErrorMessage(
                    `Solo puedes seleccionar un conector disponible o en preparación`,
                  );
                  return;
                }
                setConnectorSelected(_id);
              }}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '8px',
                padding: '14px',
                borderRadius: '10px',
                border: '1px solid #CFCFCF',
                backgroundColor: connectorSelected === _id ? '#E0F2FF' : '#fff',
              }}
            >
              <Box sx={{ display: 'flex', alignSelf: 'flex-start' }}>
                <img
                  src={plugIcons[getPlugIcon(plugType, currentType)]}
                  alt="plug"
                  style={{
                    width: '42px',
                    height: '42px',
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <Chip
                  label={translateOneStatus(ocppStatus)}
                  variant="light"
                  disabled
                  color={ocppStatusColorAndIIcon[0]}
                  backgroundColor={ocppStatusColorAndIIcon[1]}
                  iconName={ocppStatusColorAndIIcon[2]}
                />
                <Typography
                  variant="p"
                  color={TucarTheme.colors.gray700}
                  fontSize={16}
                  weight="semi-bold"
                >
                  {plugType}-{maxCapacity} kW
                </Typography>
                <Typography variant="p" color={TucarTheme.colors.gray700} fontSize={13}>
                  {translateCurrentTypeToVelocity(currentType)}
                </Typography>
              </Box>
            </Box>
          );
        })}
        <Button
          variant="contained"
          color="primary"
          onClick={handleStartCharge}
          loading={startChargingLoading}
          style={{ marginTop: '30px' }}
        >
          Iniciar carga
        </Button>
      </Box>
      {errorMessage && (
        <AlertBanner
          open={!!errorMessage}
          message={errorMessage}
          severity="error"
          onClose={() => setErrorMessage('')}
        />
      )}
    </Box>
  );
};

StationView.propTypes = {
  pin: PropTypes.shape({
    _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    plugType: PropTypes.string,
    maxCapacity: PropTypes.number,
    currentType: PropTypes.string,
    ocppStatus: PropTypes.string,
  }).isRequired,
  data: PropTypes.shape({
    provider: PropTypes.shape({
      name: PropTypes.string,
    }),
    addressAlias: PropTypes.string,
    address: PropTypes.string,
    lat: PropTypes.number,
    lon: PropTypes.number,
    connectors: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  onBack: PropTypes.func.isRequired,
};

export default StationView;
